import { networkType } from "@/config";
import { formatEther } from "ethers";
import { getCachedPoolData, getCachedWithdrawalRequestsForAddress } from "./cached-subgraph";

const SUBGRAPH_URL =
  networkType === "testnet"
    ? "https://api.goldsky.com/api/public/project_cm16apa2e540c01wz70x34xzf/subgraphs/staking-base-sepolia/0.3.0/gn"
    : "https://api.goldsky.com/api/public/project_cm16apa2e540c01wz70x34xzf/subgraphs/staking-base-mainnet/0.1.0/gn";

interface Json {
  [x: string]: string | number | boolean | Date | Json | JsonArray;
}
type JsonArray = Array<string | number | boolean | Date | Json | JsonArray>;

export const fetchSubgraphData = async <T = unknown>(
  query: string,
  variables: Json = {},
  options: { subgraphUrl: string } = { subgraphUrl: SUBGRAPH_URL }
): Promise<T> => {
  try {
    const response = await fetch(options.subgraphUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query, variables }),
      cache: "no-store",
    });
    const data = await response.json();
    if (data.errors) {
      throw new Error(JSON.stringify(data.errors));
    }
    return data.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getWithdrawalRequestsForAddressQuery = `query ($id: ID){
  users(where: {id: $id}) {
    id
    depositBalance
    isActive
    deposits {
      amount
      timestamp
    }
    withdrawRequests {
      id
      transactionHash
      timestamp
    }
    withdraws {
      id
      transactionHash
    }
  }
}`;

const getPoolDataQuery = `
  query($first: Int!) {
    thawingPoolStats(id: "global") {
      totalDeposits
      totalStakers
      activeStakers
    }
    users(first: $first, orderBy: depositBalance, orderDirection: desc) {
      id
      depositBalance
      isActive
      deposits {
        amount
        timestamp
      }
      withdrawRequests {
        id
        transactionHash
      }
      withdraws {
        id
        transactionHash
      }
    }
    transactions(first: 20, orderBy: timestamp, orderDirection: desc) {
      id
      from {
        id
      }
      eventType
      timestamp
    }
  }
`;

interface SubgraphUserData {
  users: {
    id: string;
    depositBalance: string;
    isActive: boolean;
    deposits: {
      amount: string;
      timestamp: string;
    }[];
    withdrawRequests: {
      id: string;
      transactionHash: string;
      timestamp: string;
    }[];
    withdraws: { id: string; transactionHash: string }[];
  }[];
}

export async function getWithdrawalRequestsForAddress(address: string): Promise<{
  id: string;
  depositBalance: string;
  isActive: boolean;
  deposits: {
    amount: string;
    timestamp: string;
  }[];
  withdrawRequests: {
    id: string;
    transactionHash: string;
    timestamp: string;
  }[];
  withdraws: { id: string; transactionHash: string }[];
}> {
  try {
    // Try to get cached data first
    return await getCachedWithdrawalRequestsForAddress(address);
  } catch (error) {
    console.log('Cache miss, falling back to subgraph:', error);
    // Fallback to direct subgraph call
    const response = await fetchSubgraphData<SubgraphUserData>(
      getWithdrawalRequestsForAddressQuery,
      { id: address.toLowerCase() }
    );

    return {
      ...response.users[0],
      withdrawRequests: response.users[0].withdrawRequests.sort(
        (a, b) => Number(a.timestamp) - Number(b.timestamp)
      ),
    };
  }
}

interface SubgraphPoolData {
  thawingPoolStats: {
    activeStakers: number;
    totalStakers: number;
    totalDeposits: number;
  };
  users: {
    id: string;
    depositBalance: string;
    isActive: boolean;
    deposits: {
      amount: string;
      timestamp: string;
    }[];
    withdrawRequests: {
      id: string;
      transactionHash: string;
      timestamp: string;
    }[];
    withdraws: { id: string; transactionHash: string }[];
  }[];
}

export async function getPoolData(): Promise<{
  totalDepositBalance: number;
  activeStakers: number;
}> {
  try {
    // Try to get cached data first
    return await getCachedPoolData();
  } catch (error) {
    console.log('Cache miss, falling back to subgraph:', error);
    // Fallback to direct subgraph call
    const FIRST = 1000;
    const response = await fetchSubgraphData<SubgraphPoolData>(getPoolDataQuery, {
      first: FIRST,
    });

    const activeUsers = response.users.filter((user) => user.isActive);

    const totalDepositBalance = activeUsers.reduce(
      (sum, user) => sum + BigInt(user.depositBalance),
      BigInt(0)
    );

    return {
      totalDepositBalance: Number(formatEther(totalDepositBalance)),
      activeStakers: activeUsers.length,
    };
  }
}
